import type {
  Address,
  RfqProject,
  SupplierOrg,
  SupplierShipToAddress,
  SupplierShipToPreset,
  TimberSupplierInvite,
  VendorWorkType,
} from '../types';
import { rfqProjectSupplierOrgIds } from './rfqProjectSuppliers';

export const LCTF_ASPERS_SHIP_TO: SupplierShipToAddress = {
  recipient: "Leiper's Creek Timber Frames",
  line1: '1495 Carlisle Rd',
  city: 'Aspers',
  state: 'PA',
  zip: '17304',
};

export type ProjectSiteAddressInput = {
  address?: Address;
  location?: string;
};

export type SupplierShipToConfig = {
  preset: SupplierShipToPreset;
  custom?: SupplierShipToAddress;
};

export type RfqShipToProjectRef = Partial<Pick<RfqProject, 'supplierOrgId' | 'supplierOrgIds'>>;

export type ShipToFieldContext = {
  workType: VendorWorkType;
  orgId: string;
  timberProject?: RfqShipToProjectRef | null;
  cncProject?: RfqShipToProjectRef | null;
};

export type ShipToResolveContext = ShipToFieldContext & {
  projectSite: ProjectSiteAddressInput;
};

/** True when a supplier org is assigned to both timber RFQ and CNC quote on the same deal. */
export function supplierOnBothRfqWorkTypes(
  orgId: string,
  timberProject: RfqShipToProjectRef | null | undefined,
  cncProject: RfqShipToProjectRef | null | undefined,
): boolean {
  if (!orgId) return false;
  const onTimber = !!timberProject && rfqProjectSupplierOrgIds(timberProject).includes(orgId);
  const onCnc = !!cncProject && rfqProjectSupplierOrgIds(cncProject).includes(orgId);
  return onTimber && onCnc;
}

/** Default delivery preset from RFQ engagement — not org capabilities alone. */
export function defaultShipToPreset(ctx: {
  workType: VendorWorkType;
  onBothWorkTypes: boolean;
}): SupplierShipToPreset {
  if (ctx.onBothWorkTypes) return 'job_site';
  if (ctx.workType === 'timber_rfq') return 'aspers';
  return 'job_site';
}

export function effectiveShipToPreset(
  invite: Pick<TimberSupplierInvite, 'shipToPreset' | 'shipToCustom'> | undefined,
  ctx: { workType: VendorWorkType; onBothWorkTypes: boolean },
): SupplierShipToPreset {
  const autoDefault = defaultShipToPreset(ctx);
  const stored = invite?.shipToPreset;
  if (!stored) return autoDefault;
  if (
    stored === 'job_site'
    && ctx.workType === 'timber_rfq'
    && !ctx.onBothWorkTypes
    && !hasCustomShipToContent(invite?.shipToCustom)
  ) {
    return 'aspers';
  }
  return stored;
}

export function shipToPresetLabel(preset: SupplierShipToPreset): string {
  switch (preset) {
    case 'aspers':
      return 'Aspers shop';
    case 'job_site':
      return 'Job site';
    case 'custom':
      return 'Custom address';
    default:
      return preset;
  }
}

function formatStructuredSiteLocation(addr: Address | undefined): string {
  if (!addr) return '';
  const parts: string[] = [];
  if (addr.street?.trim()) parts.push(addr.street.trim());
  const cityState = [addr.city?.trim(), addr.state?.trim()].filter(Boolean).join(', ');
  if (cityState) parts.push(cityState);
  if (addr.zipCode?.trim()) parts.push(addr.zipCode.trim());
  return parts.filter(Boolean).join(', ');
}

export function addressFromProjectSite(input: ProjectSiteAddressInput): SupplierShipToAddress | null {
  const location = (input.location || '').trim();
  const street = input.address?.street?.trim();
  const formattedFromAddress = formatStructuredSiteLocation(input.address);

  // When location was updated but structured fields lag (common after autocomplete edits),
  // treat the location string as canonical rather than stale street/city parts.
  if (location && (!street || (formattedFromAddress && formattedFromAddress !== location))) {
    return { line1: location, city: '', state: '', zip: '' };
  }

  if (street) {
    return {
      line1: street,
      city: (input.address?.city || '').trim(),
      state: (input.address?.state || '').trim(),
      zip: (input.address?.zipCode || '').trim(),
    };
  }
  if (!location) return null;
  return { line1: location, city: '', state: '', zip: '' };
}

export function hasProjectSiteAddress(input: ProjectSiteAddressInput): boolean {
  return addressFromProjectSite(input) != null;
}

/** True when a custom ship-to has any usable field (Street is not required). */
export function hasCustomShipToContent(
  addr: SupplierShipToAddress | null | undefined,
): boolean {
  if (!addr) return false;
  return Boolean(
    addr.recipient?.trim()
    || addr.line1?.trim()
    || addr.line2?.trim()
    || addr.city?.trim()
    || addr.state?.trim()
    || addr.zip?.trim(),
  );
}

export function inviteShipToConfig(
  invite: Pick<TimberSupplierInvite, 'shipToPreset' | 'shipToCustom'> | undefined,
  ctx: ShipToFieldContext,
): SupplierShipToConfig {
  const onBothWorkTypes = supplierOnBothRfqWorkTypes(ctx.orgId, ctx.timberProject, ctx.cncProject);
  const preset = effectiveShipToPreset(invite, { workType: ctx.workType, onBothWorkTypes });
  if (preset === 'custom') {
    return { preset, custom: invite?.shipToCustom ?? { line1: '', city: '', state: '', zip: '' } };
  }
  return { preset };
}

export function resolveSupplierShipToAddress(
  invite: Pick<TimberSupplierInvite, 'shipToPreset' | 'shipToCustom'> | undefined,
  ctx: ShipToResolveContext,
): SupplierShipToAddress {
  const onBothWorkTypes = supplierOnBothRfqWorkTypes(ctx.orgId, ctx.timberProject, ctx.cncProject);
  const preset = effectiveShipToPreset(invite, { workType: ctx.workType, onBothWorkTypes });
  if (preset === 'aspers') return { ...LCTF_ASPERS_SHIP_TO };
  if (preset === 'custom' && hasCustomShipToContent(invite?.shipToCustom)) {
    return { ...invite!.shipToCustom! };
  }
  const jobSite = addressFromProjectSite(ctx.projectSite);
  if (jobSite) return jobSite;
  return { ...LCTF_ASPERS_SHIP_TO };
}

export function rfqProjectSupplierShipTo(
  project: Pick<RfqProject, 'timberSupplierInvites'>,
  orgId: string,
  ctx: ShipToResolveContext,
): SupplierShipToAddress {
  const invite = project.timberSupplierInvites?.find(row => row.supplierOrgId === orgId);
  return resolveSupplierShipToAddress(invite, { ...ctx, orgId });
}

export function formatSupplierShipToAddress(addr: SupplierShipToAddress): string {
  const lines: string[] = [];
  if (addr.recipient?.trim()) lines.push(addr.recipient.trim());
  if (addr.line1?.trim()) lines.push(addr.line1.trim());
  if (addr.line2?.trim()) lines.push(addr.line2.trim());
  const cityLine = [addr.city, addr.state].filter(Boolean).join(', ');
  const cityZip = [cityLine, addr.zip].filter(Boolean).join(' ').trim();
  if (cityZip) lines.push(cityZip);
  return lines.join(', ');
}

export function formatSupplierShipToHtml(addr: SupplierShipToAddress): string {
  const parts: string[] = [];
  if (addr.recipient?.trim()) parts.push(`<strong>${escapeHtml(addr.recipient.trim())}</strong>`);
  if (addr.line1?.trim()) parts.push(escapeHtml(addr.line1.trim()));
  if (addr.line2?.trim()) parts.push(escapeHtml(addr.line2.trim()));
  const cityLine = [addr.city, addr.state].filter(Boolean).join(', ');
  const cityZip = [cityLine, addr.zip].filter(Boolean).join(' ').trim();
  if (cityZip) parts.push(escapeHtml(cityZip));
  return parts.join('<br/>');
}

/**
 * Supplier portal package row — prefer the ship-to snapshotted at publish time.
 * Live recalculation defaults timber-only RFQs to Aspers whenever a project site
 * exists, which would hide the job-site address suppliers were sent.
 */
export function vendorPortalDeliveryAddress(
  pkg: { shipToAddress?: SupplierShipToAddress | null },
  live: SupplierShipToAddress | null,
): SupplierShipToAddress | null {
  if (hasCustomShipToContent(pkg.shipToAddress)) {
    return pkg.shipToAddress!;
  }
  return live;
}

export function buildShipToByOrgId(
  project: Pick<RfqProject, 'timberSupplierInvites'>,
  orgIds: string[],
  ctx: Omit<ShipToResolveContext, 'orgId'>,
): Record<string, SupplierShipToAddress> {
  return Object.fromEntries(
    orgIds.map(orgId => [
      orgId,
      rfqProjectSupplierShipTo(project, orgId, { ...ctx, orgId }),
    ]),
  );
}

export function shipToContextForWorkType(
  workType: VendorWorkType,
  timberProject: RfqShipToProjectRef | null | undefined,
  cncProject: RfqShipToProjectRef | null | undefined,
): Pick<ShipToFieldContext, 'workType' | 'timberProject' | 'cncProject'> {
  return {
    workType,
    timberProject: timberProject ?? null,
    cncProject: cncProject ?? null,
  };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
